import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { buildVersionLabel } from './utils';
import { log } from '../../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Encapsulates the "Preview My Sticker" state machine:
 *
 *   - `previewVersions`  — history array of generated proofs ([{previewUrl, cleanUrl, …}])
 *   - `activeVersion`    — index of the version the UI is currently showing
 *   - `previewLoading`   — in-flight flag for the proof+clean blob fetches
 *   - `previewError`     — user-facing error (already massaged from the Blob body)
 *   - `loadPreview()`    — generate a new version for the active item
 *   - `clearVersions()`  — reset history + revoke object URLs (used when
 *                          the customer switches items or navigates away)
 *
 * Object URLs are revoked only on unmount to avoid blanking the IMG tag
 * mid-render (the same pattern the legacy inline logic used).
 */
export function usePreviewVersions({ orderId, activeItemId, order, onLoaded }) {
  const [previewVersions, setPreviewVersions] = useState([]);
  const [activeVersion, setActiveVersion] = useState(0);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  // Keep a ref so the unmount cleanup sees the latest versions without
  // making the effect depend on `previewVersions` (which would revoke
  // URLs on every append).
  const versionsRef = useRef([]);
  useEffect(() => { versionsRef.current = previewVersions; }, [previewVersions]);
  useEffect(() => () => {
    versionsRef.current.forEach((v) => {
      if (v.previewUrl) URL.revokeObjectURL(v.previewUrl);
      if (v.cleanUrl) URL.revokeObjectURL(v.cleanUrl);
    });
  }, []);

  const clearVersions = useCallback(() => {
    setPreviewVersions((versions) => {
      versions.forEach((v) => {
        if (v.previewUrl) URL.revokeObjectURL(v.previewUrl);
        if (v.cleanUrl) URL.revokeObjectURL(v.cleanUrl);
      });
      return [];
    });
    setActiveVersion(0);
    setPreviewLoading(false);
    setPreviewError(null);
  }, []);

  const loadPreview = useCallback(async () => {
    if (!orderId) return;
    setPreviewLoading(true);
    setPreviewError(null);

    // Multi-item: scope the preview to a single item.
    const qs = activeItemId ? `?item_id=${encodeURIComponent(activeItemId)}` : '';

    try {
      const response = await axios.get(
        `${API_URL}/api/enhanced-orders/${orderId}/proof-preview${qs}`,
        { responseType: 'blob' },
      );
      const url = URL.createObjectURL(response.data);

      // Clean-sticker variant powers the hover cross-fade. Non-fatal.
      let cleanUrlForVersion = null;
      try {
        const cleanResp = await axios.get(
          `${API_URL}/api/enhanced-orders/${orderId}/clean-sticker-preview${qs}`,
          { responseType: 'blob' },
        );
        cleanUrlForVersion = URL.createObjectURL(cleanResp.data);
      } catch (err) {
        log.warn('Clean sticker preview load failed (hover effect disabled):', err);
      }

      setPreviewVersions((prev) => {
        const next = [
          ...prev,
          {
            previewUrl: url,
            cleanUrl: cleanUrlForVersion,
            createdAt: new Date().toISOString(),
            label: buildVersionLabel(order, prev.length),
            foilType: order?.foil_type,
            foilColorHex: order?.foil_color_hex,
            itemId: activeItemId,
          },
        ];
        setActiveVersion(next.length - 1);
        return next;
      });

      if (onLoaded) onLoaded();
    } catch (error) {
      // Blob error responses carry the JSON body inside a Blob — read
      // as text so the user sees the real detail.
      let msg;
      const data = error.response?.data;
      if (data instanceof Blob) {
        try {
          const txt = await data.text();
          const parsed = JSON.parse(txt);
          msg = parsed?.detail || parsed?.message || txt;
        } catch {
          msg = error.response?.statusText || 'Could not generate preview. Please try again.';
        }
      } else {
        msg = data?.detail || error.response?.statusText || error.message
            || 'Could not generate preview. Please try again.';
      }
      log.error('Proof preview generation failed:', error);
      setPreviewError(typeof msg === 'string' ? msg : 'Could not generate preview.');
    } finally {
      setPreviewLoading(false);
    }
  }, [orderId, activeItemId, order, onLoaded]);

  return {
    previewVersions,
    activeVersion,
    setActiveVersion,
    previewLoading,
    previewError,
    setPreviewError,
    loadPreview,
    clearVersions,
  };
}
